import React, { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import {
  Box,
  Card,
  CardContent,
  Typography,
  List,
  ListItemButton,
  ListItemIcon,
  ListItemText,
  Chip,
  CircularProgress,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Stack,
  Switch,
  FormControlLabel,
  Button,
  Divider,
} from "@mui/material";
import NewReleasesIcon from "@mui/icons-material/NewReleases";
import TrendingUpIcon from "@mui/icons-material/TrendingUp";
import InfoIcon from "@mui/icons-material/Info";
import CheckCircleIcon from "@mui/icons-material/CheckCircle";
import { getAlerts, markAlertRead } from "../services/api";
import { trackEvent } from "../services/analytics";
import type { PipelineAlert } from "../types";

const alertIcons: Record<string, React.ReactNode> = {
  new_drug: <NewReleasesIcon color="primary" />,
  phase_change: <TrendingUpIcon color="success" />,
  status_change: <InfoIcon color="warning" />,
};

const alertLabels: Record<string, string> = {
  new_drug: "New Candidate",
  phase_change: "Phase Advancement",
  status_change: "Status Change",
};

export default function Alerts() {
  const navigate = useNavigate();
  const [alerts, setAlerts] = useState<PipelineAlert[]>([]);
  const [total, setTotal] = useState(0);
  const [loading, setLoading] = useState(true);
  const [unreadOnly, setUnreadOnly] = useState(true);
  const [typeFilter, setTypeFilter] = useState("");

  useEffect(() => {
    async function load() {
      setLoading(true);
      try {
        const data = await getAlerts({
          unread_only: unreadOnly,
          alert_type: typeFilter || undefined,
        });
        setAlerts(data.results);
        setTotal(data.total);
      } catch (err) {
        console.error(err);
      } finally {
        setLoading(false);
      }
    }
    load();
  }, [unreadOnly, typeFilter]);

  const handleMarkRead = async (alertId: number) => {
    try {
      trackEvent("mark_alert_read", { alert_id: alertId });
      await markAlertRead(alertId);
      setAlerts((prev) =>
        prev.map((a) => (a.id === alertId ? { ...a, is_read: true } : a))
      );
    } catch (err) {
      console.error(err);
    }
  };

  return (
    <Box>
      <Typography variant="h4" sx={{ mb: 3 }}>
        Pipeline Alerts
      </Typography>

      {/* Filters */}
      <Card sx={{ mb: 3 }}>
        <CardContent>
          <Stack direction="row" spacing={3} alignItems="center">
            <FormControlLabel
              control={
                <Switch
                  checked={unreadOnly}
                  onChange={(e) => setUnreadOnly(e.target.checked)}
                />
              }
              label="Unread only"
            />
            <FormControl size="small" sx={{ minWidth: 200 }}>
              <InputLabel>Alert Type</InputLabel>
              <Select
                value={typeFilter}
                label="Alert Type"
                onChange={(e) => setTypeFilter(e.target.value)}
              >
                <MenuItem value="">All Types</MenuItem>
                <MenuItem value="new_drug">New Candidates</MenuItem>
                <MenuItem value="phase_change">Phase Changes</MenuItem>
                <MenuItem value="status_change">Status Changes</MenuItem>
              </Select>
            </FormControl>
            <Typography variant="body2" color="text.secondary">
              {total} alert{total !== 1 ? "s" : ""}
            </Typography>
          </Stack>
        </CardContent>
      </Card>

      {/* Alerts List */}
      <Card>
        {loading ? (
          <Box sx={{ display: "flex", justifyContent: "center", py: 6 }}>
            <CircularProgress />
          </Box>
        ) : alerts.length === 0 ? (
          <CardContent>
            <Box sx={{ textAlign: "center", py: 4 }}>
              <CheckCircleIcon
                sx={{ fontSize: 48, color: "success.main", mb: 1 }}
              />
              <Typography color="text.secondary">
                {unreadOnly
                  ? "All caught up! No unread alerts."
                  : "No alerts found."}
              </Typography>
            </Box>
          </CardContent>
        ) : (
          <List>
            {alerts.map((alert, idx) => (
              <React.Fragment key={alert.id}>
                {idx > 0 && <Divider />}
                <ListItemButton
                  sx={{
                    backgroundColor: alert.is_read ? "transparent" : "#f5f9ff",
                    py: 2,
                  }}
                  onClick={() => navigate(`/pipeline/${alert.drug_candidate_id}`)}
                >
                  <ListItemIcon>
                    {alertIcons[alert.type] || <InfoIcon />}
                  </ListItemIcon>
                  <ListItemText
                    primary={
                      <Box
                        sx={{
                          display: "flex",
                          alignItems: "center",
                          gap: 1,
                        }}
                      >
                        <Typography
                          fontWeight={alert.is_read ? 400 : 700}
                          variant="body1"
                        >
                          {alert.message}
                        </Typography>
                      </Box>
                    }
                    secondary={
                      <Box
                        sx={{
                          display: "flex",
                          gap: 1,
                          mt: 0.5,
                          alignItems: "center",
                        }}
                      >
                        <Chip
                          label={alertLabels[alert.type] || alert.type}
                          size="small"
                          variant="outlined"
                          sx={{ height: 22 }}
                        />
                        <Typography variant="caption" color="text.secondary">
                          {new Date(alert.created_at).toLocaleDateString(
                            "en-US",
                            {
                              month: "short",
                              day: "numeric",
                              year: "numeric",
                              hour: "numeric",
                              minute: "2-digit",
                            }
                          )}
                        </Typography>
                      </Box>
                    }
                  />
                  {!alert.is_read && (
                    <Button
                      size="small"
                      onClick={(e) => {
                        e.stopPropagation();
                        handleMarkRead(alert.id);
                      }}
                    >
                      Mark Read
                    </Button>
                  )}
                </ListItemButton>
              </React.Fragment>
            ))}
          </List>
        )}
      </Card>
    </Box>
  );
}
